import cx from 'classnames';
import * as React from 'react';

import ClickEventTrigger from 'components/ClickEventTrigger';
import getPropClasses from 'lib/getPropClasses';
import LinkBase from 'components/LinkBase';
import LoadingIndicator, {
  LoadingIndicatorSizeEnum,
  LoadingIndicatorColorEnum,
} from 'components/LoadingIndicator';
import tooltipable from 'lib/tooltipable';

import './styles/Button.scss';

import { Props as LinkBaseProps } from 'components/LinkBase';

const propsToClasses = {
  level: {
    primary: 'Button--primary',
    secondary: 'Button--secondary',
    text: 'Button--text',
    icon: 'Button--icon',
  },
  color: {
    lava: 'Button--lava',
    caribbean: 'Button--caribbean',
    sunrise: 'Button--sunrise',
    shamrock: 'Button--shamrock',
    jungle: 'Button--jungle',
    brand: 'Button--brand',
    violet: 'Button--violet',
    pitch: 'Button--pitch',
    thunder: 'Button--thunder',
    eclipse: 'Button--eclipse',
    koala: 'Button--koala',
    'polar-bear': 'Button--polar-bear',
    white: 'Button--white',
    cactus: 'Button--cactus',
    koolaid: 'Button--koolaid',
  },
  shape: {
    circle: 'Button--circle',
    pill: 'Button--pill',
  },
  size: {
    large: 'Button--large',
    small: 'Button--small',
    xsmall: 'Button--xsmall',
    xxsmall: 'Button--xxsmall',
    xxxsmall: 'Button--xxxsmall',
  },
  weight: {
    normal: 'Button--normal',
    semibold: 'Button--semibold',
  },
  full: 'Button--full',
};

export enum ButtonSize {
  large = 'large',
  medium = 'medium',
  small = 'small',
  xsmall = 'xsmall',
  xxsmall = 'xxsmall',
  xxxsmall = 'xxxsmall',
}

export type ButtonSizeUnion = keyof typeof ButtonSize;

export enum ButtonColor {
  lava = 'lava',
  sunrise = 'sunrise',
  shamrock = 'shamrock',
  jungle = 'jungle',
  caribbean = 'caribbean',
  brand = 'brand',
  violet = 'violet',
  pitch = 'pitch',
  thunder = 'thunder',
  eclipse = 'eclipse',
  koala = 'koala',
  polarBear = 'polar-bear',
  white = 'white',
  cactus = 'cactus',
}

export enum CoachingButtonColor {
  ink = 'ink',
  koolaid = 'koolaid',
}

// no way to make this into a generic function because enums are stupid.
export type ButtonColorUnion =
  | Exclude<keyof typeof ButtonColor, 'polarBear'>
  | 'polar-bear'
  | keyof typeof CoachingButtonColor;

export type ButtonLevel = 'primary' | 'secondary' | 'text' | 'icon';

export type ButtonWeight = 'normal' | 'semibold';

export type Props = LinkBaseProps & {
  level?: ButtonLevel;
  color?: ButtonColor | ButtonColorUnion; // because you can't just say 'white' in the passed in props unless its a union.
  'aria-label'?: string;
  'data-test'?: string;
  eventContextName?: string;
  eventElementName?: string;
  eventKey?: string;
  eventProperties?: any;
  eventScreenName?: string;
  full?: boolean;
  id?: string;
  inline?: boolean;
  loading?: boolean;
  shape?: 'circle' | 'pill';
  size?: ButtonSize | ButtonSizeUnion;
  type?: 'button' | 'submit' | 'reset' | undefined;
  weight?: 'normal' | 'semibold';
  children?: React.ReactNode;
};

// Refrain from making this fancier with TypeScript,
// e.g. Pick<Props, 'full' | ... > as it will f-up
// yarn codegen-flow src/components/Button.tsx
type DefaultProps = {
  full: boolean;
  inline: boolean;
  level: ButtonLevel;
  loading: boolean;
  size: ButtonSize;
  disabled: boolean;
  download: boolean;
};

class Button extends React.Component<Props> {
  static defaultProps: DefaultProps = {
    ...LinkBase.defaultProps,
    full: false,
    inline: false,
    level: 'primary',
    loading: false,
    size: ButtonSize.small,
  };

  isDisabled = () => {
    return this.props.disabled || this.props.loading;
  };

  renderChildren = () => {
    const lightBackground = this.props.color === 'white' || this.props.level === 'secondary';
    const { size } = this.props;

    if (this.props.loading) {
      const color = lightBackground ? ButtonColor.eclipse : ButtonColor.white;
      return (
        <LoadingIndicator
          size={buttonSizeToLoadingIndicatorSizeEnum(size)}
          color={buttonColorToLoadingIndicatorColorEnum(color)}
        />
      );
    } else {
      return this.props.children;
    }
  };

  render() {
    const hasTarget = this.props.to || this.props.href;
    const Component = hasTarget ? LinkBase : 'button';

    return (
      <ClickEventTrigger
        eventElementName={this.props.eventElementName}
        eventKey={this.props.eventKey}
        eventProperties={this.props.eventProperties}
        eventScreenName={this.props.eventScreenName}>
        <Component
          id={this.props.id}
          type={this.props.type}
          target={this.props.target}
          href={this.props.href}
          to={this.props.to}
          aria-label={this.props['aria-label']}
          download={this.props.download}
          onMouseEnter={this.props.onMouseEnter}
          onMouseLeave={this.props.onMouseLeave}
          onMouseMove={this.props.onMouseMove}
          data-test-selector={this.props['data-test-selector']}
          data-test={this.props['data-test']}
          onClick={this.props.onClick}
          disabled={this.isDisabled()}
          className={cx(
            'Button',
            this.props.className,
            {
              'Button--inline': this.props.inline,
              'Button--disabled': this.isDisabled(),
            },
            getPropClasses(this.props, propsToClasses)
          )}>
          {this.renderChildren() || null}
        </Component>
      </ClickEventTrigger>
    );
  }
}

function buttonSizeToLoadingIndicatorSizeEnum(
  size?: ButtonSize | ButtonSizeUnion
): LoadingIndicatorSizeEnum {
  switch (size) {
    case ButtonSize.large:
      return LoadingIndicatorSizeEnum.large;

    case ButtonSize.small:
      return LoadingIndicatorSizeEnum.small;

    case ButtonSize.xsmall:
      return LoadingIndicatorSizeEnum.xsmall;
    default:
      return LoadingIndicatorSizeEnum.default;
  }
}

function buttonColorToLoadingIndicatorColorEnum(
  color?: ButtonColor | ButtonColorUnion
): LoadingIndicatorColorEnum {
  switch (color) {
    case ButtonColor.lava:
      return LoadingIndicatorColorEnum.lava;

    case ButtonColor.eclipse:
      return LoadingIndicatorColorEnum.eclipse;

    case ButtonColor.koala:
      return LoadingIndicatorColorEnum.koala;

    case ButtonColor.white:
      return LoadingIndicatorColorEnum.white;

    default:
      return LoadingIndicatorColorEnum.default;
  }
}

export { Button };
export default tooltipable(Button);
